import React, { useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

const PaymentMethodsCard = ({
  paymentMethods = {},
  onPaymentMethodChange,
  invoiceSettings = {}
}) => {
  const { companyId } = useParams();
  const [touchedFields, setTouchedFields] = useState({});

  // Mark field as touched
  const handleFieldTouch = useCallback((fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  }, []);

  // Handle payment method toggle
  const handleMethodToggle = useCallback((method) => {
    handleFieldTouch('paymentMethods');
    onPaymentMethodChange(method);
  }, [onPaymentMethodChange, handleFieldTouch]);

  // Validate payment methods against settings
  const validatePaymentMethod = useCallback((method) => {
    if (!paymentMethods[method]) return true;

    switch (method) {
      case 'bankgiro':
        return !!(invoiceSettings?.paymentMethods?.bankgiroNumber);
      case 'plusgiro':
        return !!(invoiceSettings?.paymentMethods?.plusgiroNumber);
      case 'paypal':
        return !!(invoiceSettings?.paymentMethods?.paypalEmail);
      case 'swish':
        return !!(invoiceSettings?.paymentMethods?.swishNumber);
      default:
        return true;
    }
  }, [paymentMethods, invoiceSettings]);

  // Check if any method has warnings
  const hasWarnings = useMemo(() => {
    return Object.keys(paymentMethods)
      .filter(method => paymentMethods[method])
      .some(method => !validatePaymentMethod(method));
  }, [paymentMethods, validatePaymentMethod]);

  // Check if any method is selected
  const hasSelectedMethods = useMemo(() => {
    return Object.values(paymentMethods).some(method => method);
  }, [paymentMethods]);

  const paymentMethodsData = [
    {
      key: 'bankgiro',
      title: 'Bankgiro',
      description: 'Traditionell bankbetalning via bankgironummer',
      icon: 'bi-bank',
      settingsKey: 'bankgiroNumber',
      missingText: 'Bankgironummer saknas'
    },
    {
      key: 'plusgiro',
      title: 'Plusgiro',
      description: 'Betalning via plusgirosystemet',
      icon: 'bi-bank2',
      settingsKey: 'plusgiroNumber',
      missingText: 'Plusgironummer saknas'
    },
    {
      key: 'paypal',
      title: 'PayPal',
      description: 'Digital betalning via PayPal-konto',
      icon: 'bi-paypal',
      settingsKey: 'paypalEmail',
      missingText: 'PayPal e-postadress saknas'
    },
    {
      key: 'swish',
      title: 'Swish',
      description: 'Mobil betalning via Swish-app',
      icon: 'bi-phone',
      settingsKey: 'swishNumber',
      missingText: 'Swish-nummer saknas'
    }
  ];

  return (
    <>
      <style>{`
        .payment-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 0;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .payment-header {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-bottom: 1px solid #d1fae5;
          padding: 1.5rem;
        }
        
        .payment-header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .payment-icon {
          width: 48px;
          height: 48px;
          background: #16a34a;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }
        
        .payment-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        
        .payment-subtitle {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0.25rem 0 0 0;
        }
        
        .payment-content {
          padding: 1.5rem;
        }
        
        .warning-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .warning-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }
        
        .warning-left {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .warning-title {
          font-weight: 700;
          color: #92400e;
          margin: 0;
        }
        
        .warning-text {
          font-size: 0.875rem;
          color: #92400e;
          margin: 0;
        }
        
        .btn-settings {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: white;
          color: #92400e;
          border: 1px solid #d97706;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
        }
        
        .btn-settings:hover {
          background: #f59e0b;
          color: white;
          text-decoration: none;
        }
        
        .error-banner {
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .error-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #dc2626;
        }
        
        .payment-methods {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }
        
        .payment-method {
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          position: relative;
        }
        
        .payment-method:hover {
          border-color: #16a34a;
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.1);
        }
        
        .payment-method.selected {
          border-color: #16a34a;
          background: #f0fdf4;
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
        }
        
        .payment-method.has-warning {
          border-color: #f59e0b;
          background: #fffbeb;
        }
        
        .method-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        
        .method-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .method-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          border-radius: 10px;
          color: #64748b;
          font-size: 1.25rem;
        }
        
        .payment-method.selected .method-icon {
          background: #16a34a;
          color: white;
        }
        
        .method-details h3 {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.25rem 0;
        }
        
        .method-details p {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0;
          line-height: 1.4;
        }
        
        .method-status {
          display: flex;
          align-items: center;
          gap: 0.375rem;
        }
        
        .status-indicator {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
        }
        
        .status-selected {
          background: #16a34a;
          color: white;
        }
        
        .status-unselected {
          background: #f1f5f9;
          color: #64748b;
          border: 2px solid #e2e8f0;
        }
        
        .method-warning {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 6px;
        }
        
        .warning-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .warning-icon {
          color: #d97706;
          font-size: 0.875rem;
        }
        
        .warning-message {
          font-size: 0.75rem;
          color: #92400e;
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .payment-methods {
            grid-template-columns: 1fr;
          }
          
          .payment-header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
          
          .warning-header {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }
        }
      `}</style>

      <div className="payment-card">
        {/* Header */}
        <div className="payment-header">
          <div className="payment-header-content">
            <div className="payment-icon">
              <i className="bi bi-credit-card"></i>
            </div>
            <div>
              <h2 className="payment-title">Betalningsmetoder</h2>
              <p className="payment-subtitle">
                Välj vilka betalningsalternativ som ska accepteras för denna offert.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="payment-content">
          {/* Global warnings */}
          {hasWarnings && (
            <div className="warning-banner">
              <div className="warning-header">
                <div className="warning-left">
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#d97706' }}></i>
                  <h4 className="warning-title">Betalningsuppgifter saknas</h4>
                </div>
                <Link
                  to={`/${companyId}/settings`}
                  className="btn-settings"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="bi bi-gear"></i>
                  Öppna inställningar
                </Link>
              </div>
              <p className="warning-text">
                Du har valt betalningsmetoder som saknar nödvändig information i inställningarna.
              </p>
            </div>
          )}

          {/* Error if no methods selected */}
          {!hasSelectedMethods && touchedFields.paymentMethods && (
            <div className="error-banner">
              <div className="error-content">
                <i className="bi bi-exclamation-circle"></i>
                <span>Minst en betalningsmetod måste väljas</span>
              </div>
            </div>
          )}

          {/* Payment Methods Grid */}
          <div className="payment-methods">
            {paymentMethodsData.map((method) => {
              const isSelected = paymentMethods[method.key];
              const hasWarning = isSelected && !validatePaymentMethod(method.key);

              return (
                <div
                  key={method.key}
                  className={`payment-method ${isSelected ? 'selected' : ''} ${hasWarning ? 'has-warning' : ''}`}
                  onClick={() => handleMethodToggle(method.key)}
                >
                  <div className="method-header">
                    <div className="method-info">
                      <div className="method-icon">
                        <i className={method.icon}></i>
                      </div>
                      <div className="method-details">
                        <h3>{method.title}</h3>
                        <p>{method.description}</p>
                      </div>
                    </div>
                    <div className="method-status">
                      <div className={`status-indicator ${isSelected ? 'status-selected' : 'status-unselected'}`}>
                        {isSelected ? (
                          <i className="bi bi-check"></i>
                        ) : (
                          <span></span>
                        )}
                      </div>
                    </div>
                  </div>

                  {hasWarning && (
                    <div className="method-warning">
                      <div className="warning-content">
                        <i className="bi bi-exclamation-triangle-fill warning-icon"></i>
                        <span className="warning-message">{method.missingText}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentMethodsCard;